import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Xmark } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { Switch } from 'components/ui/switch';
import { Skeleton } from 'components/ui/skeleton';
import { MemoryDialog } from 'components/blocks/memory-dialog/memory-dialog';
import { useConfig } from 'hooks/use-config';
import { useToast } from 'hooks/use-toast';
import { useDeleteMemory, useMemories } from 'features/memory/hooks/use-memory';
import { deleteMemory as deleteMemoryRequest } from 'features/memory/services/memory-service';
import { useUpdateReferenceSetting } from 'features/profile/hooks/use-knowledge-base';
import type { KnowledgeBaseFormData } from 'features/profile/types/knowledge-base';
import {
  accountOutlineButtonClassName,
  accountSectionClassName,
  accountSectionTitleClassName,
} from './account-styles';
import { AccountConfirmDialog } from './account-confirm-dialog';

// The card shows the newest few; "Manage all memories" opens the full list.
export const VISIBLE_MEMORY_COUNT = 5;

type ReferenceSetting = 'reference_saved_memories' | 'reference_chat_history';
type Memory = { id: string; text: string };

interface MemoryCardProps {
  knowledgeBase: KnowledgeBaseFormData | null;
}

/** Memory card (Figma 10596:4391) on the same memory and reference APIs as the Settings dialog. */
export function MemoryCard({ knowledgeBase }: MemoryCardProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useMemories();
  const { mutate: deleteMemory, isPending: isDeleting } = useDeleteMemory();
  const { mutateAsync: updateReferenceSetting } = useUpdateReferenceSetting();
  const [references, setReferences] = useState({
    reference_saved_memories: knowledgeBase?.reference_saved_memories ?? false,
    reference_chat_history: knowledgeBase?.reference_chat_history ?? false,
  });
  const [memoryToForget, setMemoryToForget] = useState<Memory | null>(null);
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [isForgetAllOpen, setIsForgetAllOpen] = useState(false);
  const [isForgettingAll, setIsForgettingAll] = useState(false);
  const queryClient = useQueryClient();
  const { configurationSettings } = useConfig();
  const { toast } = useToast();

  const memories: Memory[] = data?.memories ?? [];
  const savedMemoriesLabel = t('ACCOUNT_MEMORY_SWITCH', {
    defaultValue: 'Let Buddy remember details from my chats',
  });
  const chatHistoryLabel = t('ACCOUNT_REFERENCE_CHAT_HISTORY', {
    defaultValue: 'Use past chats',
  });

  // Switch right away and put it back if the API call fails (it shows its own error toast).
  const handleReferenceChange = async (setting: ReferenceSetting, value: boolean) => {
    setReferences((current) => ({ ...current, [setting]: value }));
    try {
      await updateReferenceSetting({ setting, value });
    } catch {
      setReferences((current) => ({ ...current, [setting]: !value }));
    }
  };

  const handleConfirmForget = () => {
    if (!memoryToForget) return;
    deleteMemory(memoryToForget.id, { onSettled: () => setMemoryToForget(null) });
  };

  // There is no bulk delete endpoint, so delete each memory and show one toast for all of them.
  const handleForgetEverything = async () => {
    setIsForgettingAll(true);
    try {
      await Promise.all(
        memories.map((memory) => deleteMemoryRequest(configurationSettings, memory.id))
      );
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('MEMORY_DELETED_SUCCESSFULLY'),
      });
    } catch {
      toast({ variant: 'destructive', title: t('ERROR'), description: t('SOMETHING_WENT_WRONG') });
    } finally {
      queryClient.invalidateQueries({ queryKey: ['memories'] });
      setIsForgettingAll(false);
      setIsForgetAllOpen(false);
    }
  };

  let memoryList = (
    <p className="py-3 text-sm text-grey-500 dark:text-muted-foreground">
      {t('ACCOUNT_NO_MEMORIES', { defaultValue: 'Buddy has not remembered anything yet.' })}
    </p>
  );
  if (isLoading) {
    memoryList = (
      <div data-testid="memory-loading" className="flex flex-col gap-2 py-2">
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-6 w-1/2" />
      </div>
    );
  } else if (memories.length > 0) {
    const visibleMemories = memories.slice(0, VISIBLE_MEMORY_COUNT);
    memoryList = (
      <ul>
        {visibleMemories.map((memory, index) => (
          <li
            key={memory.id}
            className={cn(
              'flex items-center justify-between gap-3 py-2',
              index < visibleMemories.length - 1 && 'border-b border-grey-100 dark:border-border'
            )}
          >
            <span className="min-w-0 break-words text-sm text-grey-950 dark:text-foreground">
              {memory.text}
            </span>
            <button
              type="button"
              aria-label={t('ACCOUNT_FORGET_MEMORY', {
                defaultValue: 'Forget: {{memory}}',
                memory: memory.text,
              })}
              onClick={() => setMemoryToForget(memory)}
              className="flex size-9 shrink-0 items-center justify-center rounded-card text-grey-600 hover:bg-grey-50 dark:text-muted-foreground dark:hover:bg-muted"
            >
              <Xmark width={16} height={16} strokeWidth={1.5} />
            </button>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <section className={cn(accountSectionClassName, 'flex flex-col gap-1 px-6 pb-4 pt-5')}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h2 className={accountSectionTitleClassName}>
            {t('ACCOUNT_MEMORY', { defaultValue: 'Memory' })}
          </h2>
          <p className="text-sm text-grey-500 dark:text-muted-foreground">
            {t('ACCOUNT_MEMORY_HINT', {
              defaultValue:
                "Buddy can remember useful details from your chats, so you don't have to repeat them.",
            })}
          </p>
        </div>
        <Switch
          aria-label={savedMemoriesLabel}
          checked={references.reference_saved_memories}
          onCheckedChange={(value) => handleReferenceChange('reference_saved_memories', value)}
          className="data-[state=checked]:bg-grey-900"
        />
      </div>

      <div className="flex items-center justify-between gap-3 border-b border-grey-100 py-3 dark:border-border">
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="text-sm font-medium leading-body text-grey-950 dark:text-foreground">
            {chatHistoryLabel}
          </p>
          <p className="text-xs leading-heading text-grey-500 dark:text-muted-foreground">
            {t('ACCOUNT_REFERENCE_CHAT_HISTORY_HINT', {
              defaultValue: 'Let Buddy look back at your earlier chats when it replies.',
            })}
          </p>
        </div>
        <Switch
          aria-label={chatHistoryLabel}
          checked={references.reference_chat_history}
          onCheckedChange={(value) => handleReferenceChange('reference_chat_history', value)}
          className="data-[state=checked]:bg-grey-900"
        />
      </div>

      {memoryList}

      <div className="mt-1 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setIsForgetAllOpen(true)}
          disabled={memories.length === 0}
          className={accountOutlineButtonClassName}
        >
          {t('ACCOUNT_FORGET_EVERYTHING', { defaultValue: 'Forget everything' })}
        </button>
        <button
          type="button"
          onClick={() => setIsManageOpen(true)}
          className={accountOutlineButtonClassName}
        >
          {t('ACCOUNT_MANAGE_MEMORIES', { defaultValue: 'Manage all memories' })}
        </button>
      </div>

      <MemoryDialog isOpen={isManageOpen} setIsOpen={setIsManageOpen} />
      <AccountConfirmDialog
        open={Boolean(memoryToForget)}
        onOpenChange={(isOpen) => {
          if (!isOpen) setMemoryToForget(null);
        }}
        title={t('ACCOUNT_FORGET_MEMORY_TITLE', { defaultValue: 'Forget this?' })}
        description={t('ACCOUNT_FORGET_MEMORY_CONFIRM', {
          defaultValue: 'Buddy will no longer remember: {{memory}}',
          memory: memoryToForget?.text ?? '',
        })}
        confirmText={t('ACCOUNT_FORGET', { defaultValue: 'Forget' })}
        isLoading={isDeleting}
        onConfirm={handleConfirmForget}
      />
      <AccountConfirmDialog
        open={isForgetAllOpen}
        onOpenChange={setIsForgetAllOpen}
        title={t('ACCOUNT_FORGET_EVERYTHING_TITLE', { defaultValue: 'Forget everything?' })}
        description={t('ACCOUNT_FORGET_EVERYTHING_CONFIRM', {
          defaultValue: 'Buddy will lose all the details it has saved. Your chats stay.',
        })}
        confirmText={t('ACCOUNT_FORGET_EVERYTHING', { defaultValue: 'Forget everything' })}
        isLoading={isForgettingAll}
        onConfirm={handleForgetEverything}
      />
    </section>
  );
}
